import { ref, watch } from 'vue'

const TTL_MS = 5 * 24 * 60 * 60 * 1000

const CONSONANTS = [
  'М', 'П', 'Б', 'Т', 'Д', 'Н', 'К', 'Г', 'Х',
  'Ф', 'В', 'С', 'З', 'Ш', 'Ж', 'Ч', 'Ц', 'Щ',
  'Л', 'Р', 'Й'
]

const VOWELS = ['А', 'О', 'У', 'Ы', 'Э', 'Я', 'Ё', 'Ю', 'И', 'Е', 'Ь']

function generate2() {
  const list = []
  for (const c of CONSONANTS) {
    for (const v of VOWELS) {
      list.push(c + v)
    }
  }
  return list
}

const SYLLABLES_2 = generate2()

let syllables3Promise = null

async function loadSyllables3() {
  if (syllables3Promise) return syllables3Promise
  syllables3Promise = (async () => {
    try {
      const resp = await fetch('/syllables3.txt')
      const text = await resp.text()
      const list = text.split('\n').map(l => l.trim()).filter(l => l && l.length >= 2)
      return list
    } catch {
      return []
    }
  })()
  return syllables3Promise
}

function pruneOld(attempts) {
  const cutoff = Date.now() - TTL_MS
  return attempts.filter(a => a.date > cutoff)
}

function pruneAll(raw) {
  const cleaned = {}
  for (const key of Object.keys(raw)) {
    const pruned = pruneOld(raw[key])
    if (pruned.length) cleaned[key] = pruned
  }
  return cleaned
}

function createStore(storageKey, syllables) {
  function loadStats() {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) return pruneAll(JSON.parse(raw))
    } catch {}
    return {}
  }

  const stats = ref(loadStats())

  watch(stats, (val) => {
    const cleaned = pruneAll(val)
    stats.value = cleaned
    localStorage.setItem(storageKey, JSON.stringify(cleaned))
  }, { deep: true })

  function getAttempts(s) {
    if (!stats.value[s]) return []
    return pruneOld(stats.value[s])
  }

  function record(s, correct, timeMs) {
    if (!stats.value[s]) stats.value[s] = []
    stats.value[s].push({ correct, time: timeMs, date: Date.now() })
  }

  function getStat(s) {
    const attempts = getAttempts(s)
    let correct = 0, wrong = 0, totalTime = 0
    for (const a of attempts) {
      if (a.correct) correct++
      else wrong++
      totalTime += a.time || 0
    }
    return { correct, wrong, totalTime }
  }

  function avgTime(s) {
    const attempts = getAttempts(s)
    if (!attempts.length) return 0
    const sum = attempts.reduce((acc, a) => acc + (a.time || 0), 0)
    return Math.round(sum / attempts.length / 1000)
  }

  function successRate(s) {
    const attempts = getAttempts(s)
    if (!attempts.length) return -1
    return attempts.filter(a => a.correct).length / attempts.length
  }

  function colorForRate(rate) {
    if (rate < 0) return '#e0e0e0'
    const hue = Math.round(rate * 120)
    return `hsl(${hue}, 70%, 55%)`
  }

  function totalAttempts(s) {
    return getAttempts(s).length
  }

  function wrongCount(s) {
    return getAttempts(s).filter(a => !a.correct).length
  }

  function pickNext(recentHistory) {
    const recent = new Set(recentHistory.slice(-30))
    const candidates = syllables.filter(s => !recent.has(s))
    if (candidates.length === 0) return syllables[Math.floor(Math.random() * syllables.length)]

    candidates.sort((a, b) => {
      const wa = wrongCount(a), wb = wrongCount(b)
      if (wa !== wb) return wb - wa
      return totalAttempts(a) - totalAttempts(b)
    })

    const top = candidates.slice(0, Math.max(3, Math.ceil(candidates.length * 0.3)))
    return top[Math.floor(Math.random() * top.length)]
  }

  function resetStats() {
    stats.value = {}
  }

  return {
    consonants: CONSONANTS,
    vowels: VOWELS,
    allSyllables: () => syllables,
    stats,
    record,
    getStat,
    avgTime,
    successRate,
    colorForRate,
    totalAttempts,
    wrongCount,
    pickNext,
    resetStats,
  }
}

const cache = new Map()

function getStore(key, syllables) {
  if (!cache.has(key)) {
    cache.set(key, createStore(key, syllables))
  }
  return cache.get(key)
}

export function useSyllableStats() {
  return getStore('timurka-stats-2', SYLLABLES_2)
}

export async function useSyllableStats3() {
  const syllables = await loadSyllables3()
  return getStore('timurka-stats-3', syllables)
}
